import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { RotateCcw, Trash2, UserCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { DataTable, type Column } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { Input, Select } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ViewToggle } from '@/components/ui/view-toggle';
import { useRowSelection } from '@/hooks/useRowSelection';
import { PERSONAS_LIST_POLL_MS, PLATFORM_FILTERS } from '@/constants/personas';
import { api } from '@/lib/api';
import { archetypeLabel, formatRelative, shortId } from '@/lib/format';
import type { Persona, ArchetypeSlug } from '@/types/persona';
import type { BatchOp, Platform, TrashView } from '@/types/common';

export function PersonasListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [view, setView] = useState<TrashView>('active');
  const [archetype, setArchetype] = useState<'all' | ArchetypeSlug>('all');
  const [platform, setPlatform] = useState<'all' | Platform>('all');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const selection = useRowSelection();

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => window.clearTimeout(id);
  }, [q]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQ, archetype, platform, pageSize, view]);

  useEffect(() => {
    selection.clear();
  }, [view, selection.clear]);

  // Active-niche archetypes only — same list the trigger form offers.
  const archetypesQ = useQuery({
    queryKey: ['archetypes'],
    queryFn: () => api.archetypes(),
    staleTime: 5 * 60_000,
  });

  const personasQ = useQuery({
    queryKey: ['personas', 'paginated', view, debouncedQ, archetype, platform, page, pageSize],
    queryFn: () => {
      const params: {
        q?: string;
        archetype?: string;
        platform?: string;
        page: number;
        page_size: number;
        view: TrashView;
      } = {
        page,
        page_size: pageSize,
        view,
      };
      if (debouncedQ) params.q = debouncedQ;
      if (archetype !== 'all') params.archetype = archetype;
      if (platform !== 'all') params.platform = platform;
      return api.listPersonasPaginated(params);
    },
    refetchInterval: PERSONAS_LIST_POLL_MS,
    placeholderData: (previousData) => previousData,
  });

  const filtered = personasQ.data?.items ?? [];
  const totalCount = personasQ.data?.total_count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const batchM = useMutation({
    mutationFn: ({ ids, op }: { ids: string[]; op: BatchOp }) => api.batchPersonas(ids, op),
    onSuccess: (res, { op }) => {
      const verb = op === 'restore' ? 'restored' : op === 'purge' ? 'permanently deleted' : 'moved to Trash';
      toast.success(`${res.affected} persona${res.affected === 1 ? '' : 's'} ${verb}`);
      setPurgeOpen(false);
      selection.clear();
      void queryClient.invalidateQueries({ queryKey: ['personas'] });
      void queryClient.invalidateQueries({ queryKey: ['content'] });
    },
    onError: (err) => toast.error('Bulk action failed', { description: String(err) }),
  });

  const selectedIds = [...selection.selectedIds];

  const runBatch = (op: BatchOp) => {
    if (selectedIds.length === 0) return;
    if (op === 'purge') {
      setPurgeOpen(true);
      return;
    }
    batchM.mutate({ ids: selectedIds, op });
  };

  useEffect(() => {
    if (!personasQ.data) return;
    if (page > totalPages) setPage(totalPages);
  }, [personasQ.data, page, totalPages]);

  const columns: Column<Persona>[] = [
    {
      key: 'avatar',
      header: '',
      width: '64px',
      cell: (p) => (
        <Avatar
          src={p.avatar_references?.image_urls?.[0] ?? null}
          alt={p.voice_sheet?.display_name ?? p.persona_id}
          size="md"
        />
      ),
    },
    {
      key: 'name',
      header: 'Name',
      cell: (p) => (
        <div>
          <div className="font-medium">{p.voice_sheet?.display_name ?? '—'}</div>
          <div className="font-mono text-xs text-muted-foreground">{shortId(p.persona_id)}</div>
        </div>
      ),
    },
    {
      key: 'archetype',
      header: 'Archetype',
      cell: (p) => <Badge tone="neutral">{archetypeLabel(p.archetype)}</Badge>,
    },
    {
      key: 'platform',
      header: 'Platform',
      cell: (p) => <span className="text-xs text-muted-foreground">{p.target_platform}</span>,
    },
    {
      key: 'language',
      header: 'Lang',
      cell: (p) => <span className="text-xs uppercase">{p.language}</span>,
    },
    {
      key: 'images',
      header: 'Images',
      cell: (p) => <span className="text-xs">{p.avatar_references?.image_urls?.length ?? 0}</span>,
    },
    {
      key: 'created',
      header: 'Created',
      cell: (p) => <span className="text-xs text-muted-foreground">{formatRelative(p.created_at)}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Personas</h1>
          <p className="text-sm text-muted-foreground">
            Generated identities used as the seed for content. {filtered.length} of {totalCount} shown.
          </p>
        </div>
        <Link
          to="/triggers/persona"
          search={archetype !== 'all' ? { archetype } : {}}
        >
          <Button>Generate persona</Button>
        </Link>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <ViewToggle value={view} onChange={setView} />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search voice sheet, bio, traits…"
          className="max-w-sm"
        />
        <Select value={archetype} onChange={(e) => setArchetype(e.target.value as typeof archetype)} className='max-w-[200px]'>
          <option value="all">All archetypes</option>
          {(archetypesQ.data ?? []).map((a) => (
            <option key={a.slug} value={a.slug}>
              {archetypeLabel(a.slug)}
            </option>
          ))}
        </Select>
        <Select value={platform} onChange={(e) => setPlatform(e.target.value as typeof platform)} className='max-w-[200px]'>
          {PLATFORM_FILTERS.map((p) => (
            <option key={p} value={p}>
              {p === 'all' ? 'All platforms' : p}
            </option>
          ))}
        </Select>
      </div>

      {selection.count > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/30 px-4 py-2.5">
          <span className="text-sm font-medium">{selection.count} selected</span>
          <div className="ml-auto flex items-center gap-2">
            {view === 'active' ? (
              <Button
                variant="destructive"
                size="sm"
                disabled={batchM.isPending}
                onClick={() => runBatch('delete')}
              >
                <Trash2 className="h-4 w-4" /> Move to Trash
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={batchM.isPending}
                  onClick={() => runBatch('restore')}
                >
                  <RotateCcw className="h-4 w-4" /> Restore
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={batchM.isPending}
                  onClick={() => runBatch('purge')}
                >
                  <Trash2 className="h-4 w-4" /> Delete forever
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {personasQ.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </div>
      ) : (
        <DataTable
          rows={filtered}
          columns={columns}
          rowKey={(p) => p.persona_id}
          selection={{
            selectedIds: selection.selectedIds,
            onToggleRow: selection.toggleRow,
            onToggleAll: () => selection.toggleAll(filtered.map((p) => p.persona_id)),
          }}
          onRowClick={(p) => {
            void navigate({ to: '/personas/$personaId', params: { personaId: p.persona_id } });
          }}
          empty={
            <EmptyState
              icon={<UserCircle2 className="h-8 w-8" />}
              title="No personas yet"
              description="Trigger a generation to seed the first persona for this archetype."
              action={
                <Link
                  to="/triggers/persona"
                  search={archetype !== 'all' ? { archetype } : {}}
                >
                  <Button>Generate persona</Button>
                </Link>
              }
            />
          }
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Page size</span>
            <Select
              value={String(pageSize)}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="w-[96px]"
            >
              {[10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Select>
          </div>
          <div className="text-muted-foreground">
            Page {page} of {totalPages} · {totalCount} total
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={personasQ.isFetching || page <= 1}
          >
            Previous
          </Button>
          <Input
            type="number"
            min={1}
            max={totalPages}
            value={page}
            onChange={(e) => setPage(Math.min(totalPages, Math.max(1, Number(e.target.value) || 1)))}
            className="w-20"
          />
          <Button
            variant="outline"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={personasQ.isFetching || page >= totalPages}
          >
            Next
          </Button>
        </div>
      </div>

      {purgeOpen && (
        <ConfirmDialog
          title="Delete forever?"
          description={`Permanently delete ${selectedIds.length} persona${
            selectedIds.length === 1 ? '' : 's'
          } and all their linked content + video files. This cannot be undone.`}
          confirmLabel="Delete forever"
          destructive
          pending={batchM.isPending}
          onConfirm={() => batchM.mutate({ ids: selectedIds, op: 'purge' })}
          onCancel={() => setPurgeOpen(false)}
        />
      )}
    </div>
  );
}
